import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { BookOpen, MessageCircle, ArrowRight } from "lucide-react";

export default function Chapters() {
  const [chapters, setChapters] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/chapters");
        setChapters(data);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24" data-testid="chapters-page">
      <div className="animate-fade-up max-w-3xl">
        <div className="text-xs tracking-widest uppercase text-[#D96C4A]">The eighteen chapters</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-[#2D3B31] mt-3">
          Adhyāyaḥ — a library of the self.
        </h1>
        <p className="mt-5 text-[#5C6B5F] leading-relaxed max-w-2xl">
          From Arjuna's despair on the battlefield to the final promise of liberation,
          each chapter unfolds a path. Enter any one, read the verses, and join the conversation.
        </p>
      </div>

      {error && (
        <div className="mt-8 text-sm text-[#C05B3B] bg-[#F2D8CF]/50 border border-[#F2D8CF] rounded-lg px-4 py-3 max-w-lg" data-testid="chapters-error">
          {error}
        </div>
      )}

      {chapters === null ? (
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-56 rounded-2xl bg-[#F3EFE9] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="chapters-grid">
          {chapters.map((c, i) => (
            <Link
              to={`/chapters/${c.number}`}
              key={c.number}
              className="organic-card p-8 flex flex-col group animate-fade-up"
              style={{ animationDelay: `${Math.min(i * 0.05, 0.4)}s` }}
              data-testid={`chapter-card-${c.number}`}
            >
              <div className="flex items-start justify-between">
                <div className="chapter-ornament">{c.number.toString().padStart(2, "0")}</div>
                <div className="text-right">
                  <div className="text-xs tracking-widest uppercase text-[#8A948C]">Adhyāya</div>
                  <div className="text-xs text-[#8A948C] mt-1">{c.verses_count} verses</div>
                </div>
              </div>
              <div className="mt-6">
                <div className="font-serif-display text-2xl text-[#2D3B31] leading-tight">
                  {c.title}
                </div>
                <div className="text-sm text-[#8A948C] mt-1 italic font-serif-display">
                  {c.subtitle}
                </div>
              </div>
              <p className="mt-4 text-sm text-[#5C6B5F] leading-relaxed line-clamp-3">
                {c.summary}
              </p>
              <div className="mt-6 pt-5 flex items-center justify-between border-t" style={{ borderColor: "#EAE5D9" }}>
                <div className="flex items-center gap-4 text-xs text-[#5C6B5F]">
                  <span className="inline-flex items-center gap-1.5">
                    <BookOpen size={14} /> Read
                  </span>
                  <span className="inline-flex items-center gap-1.5" data-testid={`chapter-${c.number}-thread-count`}>
                    <MessageCircle size={14} /> {c.thread_count || 0} {c.thread_count === 1 ? "thread" : "threads"}
                  </span>
                </div>
                <ArrowRight
                  size={18}
                  className="text-[#D96C4A] transition-transform group-hover:translate-x-1"
                />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
